import React from 'react';
import { AttributeGroupEnum, useGlobalContext } from '@honeycomb/data';
import { Anchor, Cropper, FlexGrid, Paper, Stacker, Styler, TypographyBody, TypographyHeading, TypographyStyler, UnsafeHTML, useBreakpoints, useDisclosure, } from '@honeycomb/ui-core';
import { removeExcessBrTags, removeLineSeparators } from '../../../utils';
import { HotelFacilityIcon } from '../HotelFacilityIcon';
function HotelFacility({ facilityCode, name }) {
    return (React.createElement(Stacker, { direction: "row", alignItems: "center", spacing: 2 },
        React.createElement(HotelFacilityIcon, { facilityCode: facilityCode }),
        React.createElement(TypographyBody, { component: "span", size: "s", flexShrink: 1, "data-id": "hotel-facility-desc" }, name)));
}
const MAX_FACILITIES = {
    mobile: 4,
    desktop: 8,
};
export function HotelOverview({ hotelDetails }) {
    const { resourceStrings: { SHOW_LESS, SHOW_MORE, HOTEL_OVERVIEW_HEADING, HOTEL_FACILITIES_HEADING, SHOW_ALL, SHOW_FEWER, }, } = useGlobalContext();
    const { largeUp } = useBreakpoints();
    const { overview, attributeGroups } = hotelDetails;
    // The HTML we receive is often poorly formatted.
    // Here we remove line separators and excessive usage of <br> tags.
    const parsedOverview = React.useMemo(() => {
        return removeExcessBrTags(removeLineSeparators(overview));
    }, [overview]);
    // Isolate hotel facilities from the hotel's attributes.
    const facilities = React.useMemo(() => {
        const attributeGroup = attributeGroups === null || attributeGroups === void 0 ? void 0 : attributeGroups.find((group) => group.type === AttributeGroupEnum.Facilities);
        return (attributeGroup === null || attributeGroup === void 0 ? void 0 : attributeGroup.attributes) || [];
    }, [attributeGroups]);
    const maxFacilities = largeUp ? MAX_FACILITIES.desktop : MAX_FACILITIES.mobile;
    const [showAllFacilities, { toggle: handleToggleShowAllFacilities }] = useDisclosure(false);
    const filteredFacilities = showAllFacilities ? facilities : facilities.slice(0, maxFacilities);
    const hasFacilities = facilities.length > 0;
    const hasMoreFacilties = !showAllFacilities && facilities.length > filteredFacilities.length;
    return (React.createElement(Paper, { py: 5, defaultGutters: true },
        React.createElement(TypographyHeading, { size: "s", color: "text.body", mb: 4, "data-id": "hotel_information_heading" }, HOTEL_OVERVIEW_HEADING),
        React.createElement(Stacker, { spacing: 6 },
            React.createElement("article", null,
                React.createElement(Cropper, { buttonAlignment: "flex-start", maxHeight: 120, buttonCaption: {
                        cropped: SHOW_MORE,
                        expanded: SHOW_LESS,
                    }, "data-id": "hotel_information_cropper" },
                    React.createElement(TypographyStyler, { headingSizeMap: { h1: 's', h2: 's', h3: 's', h4: 's', h5: 's', h6: 's' } },
                        React.createElement(UnsafeHTML, { "data-id": "hotel_information_content" }, parsedOverview)))),
            hasFacilities && (React.createElement("article", null,
                React.createElement(TypographyHeading, { size: "xs", color: "text.body", mb: 4, "data-id": "hotel_facilities_heading" }, HOTEL_FACILITIES_HEADING),
                React.createElement(FlexGrid, { container: true, spacing: 4 }, filteredFacilities.map((attribute) => {
                    const { code, name } = attribute;
                    return (React.createElement(FlexGrid, { xs: 6, m: 3, key: code },
                        React.createElement(HotelFacility, { facilityCode: code, name: name })));
                })),
                (showAllFacilities || hasMoreFacilties) && (React.createElement(Styler, { mt: 4 },
                    React.createElement(Anchor, { button: true, onClick: () => handleToggleShowAllFacilities(), color: "text.link", size: "s", "data-id": "hotel_facility_toggle" }, !showAllFacilities ? SHOW_ALL : SHOW_FEWER))))))));
}
